import type { Theme } from './tokens';

/**
 * The folder colours: thirteen named colours, eight bases and five
 * variations, each a set of opaque tones per strength and theme. From the
 * Pondering design system (`tag-<id>-band|card|chip|edge|mark|ground`), the
 * same tokens the app draws, so a folder reads the same on both heads.
 *
 * The recipe is one OKLCH tone per strength and theme with the hue the only
 * difference, so every folder weighs the same on every surface:
 *
 * | strength | light           | dark            |
 * | -------- | --------------- | --------------- |
 * | band     | L 0.875 C 0.050 | L 0.330 C 0.050 |
 * | card     | L 0.952 C 0.020 | L 0.290 C 0.033 |
 * | chip     | L 0.900 C 0.040 | L 0.315 C 0.042 |
 * | edge     | L 0.58  C 0.10  | L 0.62  C 0.09  |
 * | mark     | L 0.50  C 0.12* | L 0.80  C 0.10  |
 * | ground   | L 0.935 C 0.014 | L 0.245 C 0.020 |
 *
 * *walked darker until it clears 4.6:1 on `bg`, chroma given up to stay in
 * gamut. The values are the design system's, not recomputed here;
 * `tagPalette.test.ts` holds them to the recipe.
 */

/** In the colour sheet's order: each base, then its variation. */
export const TAG_COLOR_IDS = [
  'brick',
  'tangerine',
  'olive',
  'citron',
  'fern',
  'pine',
  'lagoon',
  'aquamarine',
  'sky',
  'cobalt',
  'indigo',
  'orchid',
  'magenta',
] as const;

export type TagColorId = (typeof TAG_COLOR_IDS)[number];

export interface TagColor {
  id: TagColorId;
  /** What the colour is called wherever it is named. */
  name: string;
  /** The base a variation is kin to; null for a base. */
  base: TagColorId | null;
  /** OKLCH hue, degrees. */
  hue: number;
}

export const TAG_COLORS: Readonly<Record<TagColorId, TagColor>> = {
  brick: { id: 'brick', name: 'Brick', base: null, hue: 22.5 },
  tangerine: { id: 'tangerine', name: 'Tangerine', base: null, hue: 52.5 },
  olive: { id: 'olive', name: 'Olive', base: null, hue: 112.5 },
  citron: { id: 'citron', name: 'Citron', base: 'olive', hue: 99.5 },
  fern: { id: 'fern', name: 'Fern', base: null, hue: 142.5 },
  pine: { id: 'pine', name: 'Pine', base: 'fern', hue: 152.5 },
  lagoon: { id: 'lagoon', name: 'Lagoon', base: null, hue: 202.5 },
  // Stored as `aquamarine` because the old palette's `aqua` was another
  // colour, which now reads as Lagoon.
  aquamarine: { id: 'aquamarine', name: 'Aqua', base: 'lagoon', hue: 195.5 },
  sky: { id: 'sky', name: 'Sky', base: null, hue: 232.5 },
  cobalt: { id: 'cobalt', name: 'Cobalt', base: null, hue: 262.5 },
  indigo: { id: 'indigo', name: 'Indigo', base: 'cobalt', hue: 272.5 },
  orchid: { id: 'orchid', name: 'Orchid', base: null, hue: 322.5 },
  magenta: { id: 'magenta', name: 'Magenta', base: 'orchid', hue: 336.5 },
};

/** One folder colour in one theme. */
export interface TagTones {
  /** A folder's head, a notebook, a nook's sign, an arc placed on a canvas. */
  band: string;
  /** A recording's, note's or thread's card; a small folder. */
  card: string;
  /** A pill filled with the colour. */
  chip: string;
  /** The border of a filled chip, and of an arc drawn as a card. */
  edge: string;
  /** The folder's mark, a swatch, a canvas's dots (at 30%). */
  mark: string;
  /** The page a folder's content stands on. */
  ground: string;
}

const TONES: Readonly<Record<Theme, Readonly<Record<TagColorId, TagTones>>>> = {
  light: {
    brick: { band: '#F5CAC7', card: '#FDEAE9', chip: '#F8D4D2', edge: '#AE615F', mark: '#9C4343', ground: '#F3E6E5' },
    tangerine: { band: '#F2CEB8', card: '#FBECE3', chip: '#F5D7C6', edge: '#A8683F', mark: '#974C10', ground: '#F2E7E1' },
    olive: { band: '#D6DAB4', card: '#EFF1E2', chip: '#DEE1C3', edge: '#7B8034', mark: '#656900', ground: '#E9EBE0' },
    citron: { band: '#DDD7B2', card: '#F2F0E1', chip: '#E4DFC1', edge: '#897B2C', mark: '#726400', ground: '#EBEADF' },
    fern: { band: '#C4DFC1', card: '#E8F3E7', chip: '#CFE5CD', edge: '#568951', mark: '#347430', ground: '#E4ECE3' },
    pine: { band: '#BEE0C6', card: '#E6F3E9', chip: '#CBE6D1', edge: '#478B5D', mark: '#187640', ground: '#E3ECE5' },
    lagoon: { band: '#B0E0E4', card: '#E1F3F5', chip: '#C0E6EA', edge: '#008B94', mark: '#007178', ground: '#DFECED' },
    aquamarine: { band: '#B0E1E0', card: '#E1F4F3', chip: '#C0E7E6', edge: '#008C8D', mark: '#007273', ground: '#DFEDEC' },
    sky: { band: '#B6DCF2', card: '#E3F2FB', chip: '#C5E3F5', edge: '#2F84AB', mark: '#006C94', ground: '#E1EBF1' },
    cobalt: { band: '#C4D7F8', card: '#E8F0FD', chip: '#D0DFF9', edge: '#5A7AB6', mark: '#3D61A7', ground: '#E4EAF3' },
    indigo: { band: '#CAD5F8', card: '#EAEFFD', chip: '#D5DDF9', edge: '#6676B6', mark: '#4D5CA7', ground: '#E6E9F3' },
    orchid: { band: '#E6CBEA', card: '#F6EBF7', chip: '#EBD6EE', edge: '#96659D', mark: '#83498B', ground: '#EEE7EF' },
    magenta: { band: '#ECCAE2', card: '#F8EAF4', chip: '#F0D4E8', edge: '#A06290', mark: '#8D457C', ground: '#F0E6ED' },
  },
  dark: {
    brick: { band: '#4C2B29', card: '#3A2524', chip: '#452928', edge: '#B6706D', mark: '#F8A4A0', ground: '#291D1C' },
    tangerine: { band: '#4A2E1C', card: '#39271C', chip: '#432B1D', edge: '#B17652', mark: '#F1AC81', ground: '#281E18' },
    olive: { band: '#363818', card: '#2B2D1A', chip: '#32341A', edge: '#878C49', mark: '#BEC578', ground: '#202216' },
    citron: { band: '#3C3615', card: '#2F2C18', chip: '#373218', edge: '#938743', mark: '#CDBF72', ground: '#232116' },
    fern: { band: '#263C24', card: '#223020', chip: '#253723', edge: '#669462', mark: '#98CF93', ground: '#1B231A' },
    pine: { band: '#203D29', card: '#1F3023', chip: '#203827', edge: '#5A966C', mark: '#8BD19F', ground: '#19231C' },
    lagoon: { band: '#0C3D41', card: '#153032', chip: '#13383B', edge: '#33969E', mark: '#65D1DA', ground: '#152325' },
    aquamarine: { band: '#0C3D3D', card: '#153131', chip: '#133838', edge: '#339797', mark: '#65D2D2', ground: '#152323' },
    sky: { band: '#173A4B', card: '#1A2E39', chip: '#1A3543', edge: '#488FB3', mark: '#77C9F3', ground: '#172228' },
    cobalt: { band: '#27354F', card: '#222B3C', chip: '#263247', edge: '#6986BD', mark: '#9BBEFF', ground: '#1B212A' },
    indigo: { band: '#2D344F', card: '#262A3C', chip: '#2A3047', edge: '#7483BD', mark: '#A8BAFF', ground: '#1D202A' },
    orchid: { band: '#422C44', card: '#332635', chip: '#3C2A3E', edge: '#A074A6', mark: '#DDA8E4', ground: '#251D26' },
    magenta: { band: '#462B3F', card: '#362531', chip: '#3F293A', edge: '#A9719A', mark: '#E7A5D6', ground: '#271D24' },
  },
};

export function tagTones(theme: Theme, id: TagColorId): TagTones {
  return TONES[theme][id];
}

export function isTagColorId(value: string): value is TagColorId {
  return Object.hasOwn(TAG_COLORS, value);
}
